import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import {
  Star, MapPin, Wrench, Smartphone, ArrowRight, ArrowUpRight,
  Instagram, Youtube, Phone, MessageCircle, Navigation,
} from "lucide-react";
import { business, fullAddress } from "@/config/business";
import { images } from "@/config/site";
import { brands } from "@/data/brands";
import { featuredNew, featuredUsed } from "@/data/products";
import { repairServices } from "@/data/repairServices";
import { ProductCard } from "@/components/product/ProductCard";
import { BrandCard } from "@/components/product/BrandCard";
import { WhatsAppButton, SellPhoneWhatsAppButton } from "@/components/whatsapp/WhatsAppButton";
import { Reveal, stagger } from "@/components/ui-motion/Reveal";
import { useSeo } from "@/lib/seo";

const heroLines = ["New &", "Pre-Owned", "Smartphones"];

const HeroLine = ({ children, delay }) => (
  <span className="line-mask">
    <motion.span
      className="line-inner"
      initial={{ y: "105%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 8]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-10 sm:pt-16" data-testid="hero">
      <div className="container-wide grid items-center gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="text-xs font-semibold uppercase tracking-[0.35em] text-[hsl(var(--ink-soft))]">
            Buy • Sell • Repair
          </motion.p>

          <h1 className="mt-5 font-display text-5xl font-extrabold leading-[0.95] sm:text-6xl lg:text-7xl">
            {heroLines.map((l, i) => (
              <HeroLine key={l} delay={0.2 + i * 0.12}>{l}</HeroLine>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-6 max-w-md text-base text-[hsl(var(--ink-soft))] sm:text-lg">
            Find your next phone or get your existing device repaired by <span className="text-[hsl(var(--ink))]">Mobile Specialist</span> in Rohini.
          </motion.p>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85 }}
            className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 accent-text" /> Sector 3, Rohini, Delhi</span>
            <span className="flex items-center gap-1.5"><Star className="h-4 w-4 fill-[hsl(var(--accent))] accent-text" /> {business.googleRating}/5 on Google · {business.googleReviewCount} Reviews</span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.6 }}
            className="mt-8 flex flex-wrap gap-3">
            <Link to="/phones" className="btn-accent" data-testid="hero-browse">Browse Phones <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/sell-your-phone" className="btn-outline" data-testid="hero-sell">Sell Your Phone</Link>
            <Link to="/repair" className="btn-outline" data-testid="hero-repair">Repair Your Phone</Link>
            <WhatsAppButton label="WhatsApp Us" testId="hero-whatsapp" />
          </motion.div>
        </div>

        <motion.div style={{ y, rotate }} className="relative mx-auto max-w-sm">
          <div className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-white/60 blur-2xl" />
          <img src={images.heroPhone} alt="Flagship smartphone available at Mobile Specialist, Rohini"
            className="w-full drop-shadow-2xl" fetchpriority="high" />
        </motion.div>
      </div>
    </section>
  );
};

const trustItems = [
  { icon: Star, label: `${business.googleRating}/5 Google Rating` },
  { icon: MessageCircle, label: `${business.googleReviewCount} Customer Reviews` },
  { icon: MapPin, label: "Rohini, Delhi" },
  { icon: Wrench, label: "Mobile Repair" },
  { icon: Smartphone, label: "New & Pre-Owned Phones" },
];

const TrustBar = () => (
  <section className="container-wide mt-14" aria-label="Trust signals" data-testid="trust-bar">
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-3 lg:grid-cols-5">
      {trustItems.map((t) => (
        <div key={t.label} className="flex items-center gap-2.5 bg-card px-4 py-5 text-sm font-medium">
          <t.icon className="h-5 w-5 shrink-0 accent-text" />
          {t.label}
        </div>
      ))}
    </div>
  </section>
);

const Marquee = () => {
  const words = ["Screen Repair", "Battery Replacement", "Apple Repair", "Used iPhone", "Water Damage", "Data Recovery", "New Phones", "Sell Your Phone"];
  const row = [...words, ...words];
  return (
    <section className="mt-20 border-y border-border bg-[hsl(var(--ink))] py-4 text-white" aria-hidden="true">
      <div className="marquee-track">
        {row.map((w, i) => (
          <span key={i} className="mx-6 font-display text-xl font-semibold">
            {w} <span className="accent-text">✦</span>
          </span>
        ))}
      </div>
    </section>
  );
};

const SectionHead = ({ eyebrow, title, sub, action }) => (
  <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
    <div>
      {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[hsl(var(--ink-soft))]">{eyebrow}</p>}
      <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">{title}</h2>
      {sub && <p className="mt-2 max-w-xl text-[hsl(var(--ink-soft))]">{sub}</p>}
    </div>
    {action}
  </div>
);

const ShopByBrand = () => (
  <section className="container-wide mt-24" data-testid="shop-by-brand">
    <Reveal><SectionHead eyebrow="Shop by Brand" title="Every major brand, one shop" /></Reveal>
    <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
      className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {brands.map((b) => <BrandCard key={b.id} brand={b} />)}
    </motion.div>
  </section>
);

const ProductRow = ({ id, eyebrow, title, sub, items, to }) => (
  <section className="container-wide mt-24" data-testid={id}>
    <Reveal>
      <SectionHead eyebrow={eyebrow} title={title} sub={sub}
        action={<Link to={to} className="group hidden items-center gap-1 text-sm font-semibold sm:inline-flex">View all <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></Link>} />
    </Reveal>
    <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
      className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((p) => <ProductCard key={p.id} product={p} />)}
    </motion.div>
    <p className="mt-4 text-xs text-[hsl(var(--ink-soft))]">Demo listings shown for preview. Live stock and prices are confirmed in store or on WhatsApp.</p>
  </section>
);

const SellSection = () => (
  <section className="container-wide mt-24" data-testid="sell-section">
    <Reveal>
      <div className="relative overflow-hidden rounded-3xl bg-[hsl(var(--ink))] px-6 py-14 text-white sm:px-14">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[hsl(var(--accent))]/25 blur-3xl" />
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/50">Have an old phone?</p>
        <h2 className="mt-3 max-w-2xl font-display text-4xl font-bold sm:text-5xl">Turn your old phone into cash.</h2>
        <p className="mt-4 max-w-lg text-white/70">Tell us about your device and we'll help you with a fair valuation over WhatsApp — quick, simple and honest.</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/sell-your-phone" className="btn-accent" data-testid="sell-cta">Get a Quote <ArrowRight className="h-4 w-4" /></Link>
          <SellPhoneWhatsAppButton label="Ask on WhatsApp" className="btn-outline border-white text-white hover:bg-white hover:text-[hsl(var(--ink))]" />
        </div>
      </div>
    </Reveal>
  </section>
);

const RepairSection = () => (
  <section className="container-wide mt-24" data-testid="repair-section">
    <Reveal>
      <SectionHead eyebrow="Mobile Repair Services" title="Mobile Repair Services"
        sub="From screens and batteries to charging, cameras, software and Apple repairs."
        action={<Link to="/repair" className="btn-ink hidden sm:inline-flex">Explore Repair Services <ArrowRight className="h-4 w-4" /></Link>} />
    </Reveal>
    <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
      <Reveal className="relative overflow-hidden rounded-3xl border border-border">
        <img src={images.repairScene} alt="Technician repairing a smartphone at Mobile Specialist" loading="lazy" className="h-full w-full object-cover" />
      </Reveal>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
        {repairServices.slice(0, 12).map((s, i) => (
          <Reveal key={s.id} delay={i * 0.03} className="flex items-center rounded-xl border border-border bg-card px-3 py-3 text-sm font-medium">
            {s.name}
          </Reveal>
        ))}
      </div>
    </div>
    <Link to="/repair" className="btn-ink mt-6 inline-flex sm:hidden">Explore Repair Services <ArrowRight className="h-4 w-4" /></Link>
  </section>
);

const chapters = [
  { n: "01", t: "New & Pre-Owned", d: "A curated range of new and quality pre-owned smartphones from every major brand." },
  { n: "02", t: "Repaired Right", d: "Screens, batteries, charging, cameras, software and specialist Apple repairs." },
  { n: "03", t: "Trusted Locally", d: "Rated 4.9/5 across 323 Google reviews by customers in Rohini." },
];

const Manifesto = () => (
  <section className="container-wide mt-28" data-testid="manifesto">
    <Reveal><h2 className="max-w-3xl font-display text-3xl font-bold sm:text-4xl">Why Rohini trusts Mobile Specialist.</h2></Reveal>
    <div className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-border bg-border md:grid-cols-3">
      {chapters.map((c, i) => (
        <Reveal key={c.n} delay={i * 0.1} className="bg-card p-8">
          <span className="font-display text-5xl font-extrabold text-[hsl(var(--line))]">{c.n}</span>
          <h3 className="mt-4 text-xl font-semibold">{c.t}</h3>
          <p className="mt-2 text-sm text-[hsl(var(--ink-soft))]">{c.d}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Reviews = () => (
  <section className="container-wide mt-24" data-testid="reviews">
    <Reveal>
      <div className="flex flex-col items-center rounded-3xl border border-border bg-card px-6 py-16 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[hsl(var(--ink-soft))]">Trusted by Customers in Rohini</p>
        <div className="mt-6 flex items-center gap-4">
          <span className="font-display text-7xl font-extrabold">{business.googleRating}</span>
          <div className="text-left">
            <div className="flex gap-1">{[0,1,2,3,4].map((i) => <Star key={i} className="h-5 w-5 fill-[hsl(var(--accent))] accent-text" />)}</div>
            <p className="mt-1 text-sm text-[hsl(var(--ink-soft))]">{business.googleReviewCount} Google Reviews</p>
          </div>
        </div>
        <a href={business.googleProfileUrl} target="_blank" rel="noopener noreferrer" className="btn-ink mt-8" data-testid="view-reviews">View All Google Reviews <ArrowUpRight className="h-4 w-4" /></a>
      </div>
    </Reveal>
  </section>
);

const Social = () => (
  <section className="container-wide mt-24" data-testid="social">
    <Reveal><SectionHead eyebrow="Stay Connected" title="Follow Mobile Specialist" /></Reveal>
    <div className="grid gap-4 sm:grid-cols-2">
      <Reveal>
        <a href={business.instagramUrl} target="_blank" rel="noopener noreferrer" data-testid="social-instagram"
          className="group flex items-center justify-between rounded-2xl border border-border bg-card p-6 transition-colors hover:border-[hsl(var(--ink))]">
          <span className="flex items-center gap-4"><Instagram className="h-7 w-7" /><span><span className="block font-semibold">Instagram</span><span className="text-sm text-[hsl(var(--ink-soft))]">@mobilespecialistrohini</span></span></span>
          <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </Reveal>
      <Reveal delay={0.08}>
        <a href={business.youtubeUrl} target="_blank" rel="noopener noreferrer" data-testid="social-youtube"
          className="group flex items-center justify-between rounded-2xl border border-border bg-card p-6 transition-colors hover:border-[hsl(var(--ink))]">
          <span className="flex items-center gap-4"><Youtube className="h-7 w-7" /><span><span className="block font-semibold">YouTube</span><span className="text-sm text-[hsl(var(--ink-soft))]">Mobile Specialist Rohini</span></span></span>
          <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </Reveal>
    </div>
  </section>
);

export const LocationSection = () => (
  <section className="container-wide mt-24" data-testid="location-section">
    <Reveal>
      <div className="grid overflow-hidden rounded-3xl border border-border bg-card lg:grid-cols-2">
        <div className="p-8 sm:p-12">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[hsl(var(--ink-soft))]">Visit Us</p>
          <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">Visit Mobile Specialist</h2>
          <address className="mt-6 not-italic leading-relaxed text-[hsl(var(--ink-soft))]">
            Shop Number 3, Pocket E-18/46<br />Opposite Remal Public School<br />Sector 3, Rohini<br />Delhi - 110085
          </address>
          <p className="mt-4 text-sm">{business.hoursSummary}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={business.googleDirectionsUrl} target="_blank" rel="noopener noreferrer" className="btn-accent" data-testid="loc-directions"><Navigation className="h-4 w-4" /> Get Directions</a>
            <a href={`tel:${business.phone}`} className="btn-outline" data-testid="loc-call"><Phone className="h-4 w-4" /> Call</a>
            <WhatsAppButton label="WhatsApp" className="btn-outline" testId="loc-whatsapp" />
          </div>
        </div>
        <a href={business.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="group relative min-h-[280px] overflow-hidden border-t border-border lg:border-l lg:border-t-0" aria-label="Open store location in Google Maps">
          <img src={images.storeInterior} alt="Inside the Mobile Specialist store in Rohini" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <span className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold shadow"><MapPin className="h-4 w-4 accent-text" /> Open in Google Maps</span>
        </a>
      </div>
    </Reveal>
  </section>
);

export default function Home() {
  useSeo({
    title: "Mobile Specialist Rohini — New & Pre-Owned Phones · Mobile Repair",
    description: "Buy, sell and repair phones at Mobile Specialist, Sector 3 Rohini, Delhi. New & pre-owned smartphones and professional mobile repair. Rated 4.9/5 on Google.",
    path: "/",
    image: images.heroPhone,
  });
  return (
    <>
      <Hero />
      <TrustBar />
      <Marquee />
      <ShopByBrand />
      <ProductRow id="featured-new" eyebrow="Latest Phones" title="Latest Phones" sub="Explore new smartphones available at Mobile Specialist." items={featuredNew} to="/phones?type=new" />
      <ProductRow id="featured-used" eyebrow="Pre-Owned Phones" title="Pre-Owned Phones" sub="Quality phones at attractive prices." items={featuredUsed} to="/phones?type=used" />
      <SellSection />
      <RepairSection />
      <Manifesto />
      <Reviews />
      <Social />
      <LocationSection />
    </>
  );
}
